import { attempt } from "@omni/utils/function.utils";
import { cn } from "@omni/utils/styles";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { Flyout } from "@/components/General/Flyout/flyout";
import { SidebarContextProvider } from "@/components/General/Sidebar/context/sidebar.context";
import { useThunkDispatch } from "@/redux/dispatcher";
import * as FlyoutCategoriesSelectors from "@/redux/modules/value/flyoutCategories/flyoutCategories.selectors";
import * as HeaderModule from "@/redux/modules/value/header/header.module";
import * as HeaderSelectors from "@/redux/modules/value/header/header.selectors";

type Props = {
    accountLinks: string;
    profilelink: string;
    innerHTML?: string;
    children: ReactNode;
};

export const Base = ({
    accountLinks,
    profilelink,
    innerHTML,
    children
}: Props) => {
    const [assortimentOpen, setAssortimentOpen] = useState<boolean>(false);

    const [activeCategoryIndexes, setActiveCategoryIndexes] = useState<
        Array<number>
    >([]);

    const rootCategory = useSelector(
        FlyoutCategoriesSelectors.getFlyoutCategories
    );

    const sidebarActive = useSelector(HeaderSelectors.isSidebarActive);
    const subCategoriesActive = !!activeCategoryIndexes.length;

    const dispatch = useThunkDispatch();

    const handleOnClose = useCallback(() => {
        dispatch(HeaderModule.update({ sidebarActive: !sidebarActive }));
        setActiveCategoryIndexes([]);
        setAssortimentOpen(false);
    }, [sidebarActive, dispatch]);

    const parsedAccountLinks = useMemo(() => {
        return attempt(() => JSON.parse(accountLinks), []);
    }, [accountLinks]);

    if (!rootCategory) return null;

    return (
        <Flyout
            open={sidebarActive}
            onClose={handleOnClose}
            overlayHeader={false}
            className={cn("tw:overflow-hidden tw:relative")}
        >
            <Flyout.Content
                className={cn(
                    "sidebar",
                    "tw:p-0 tw:overflow-x-hidden tw:relative",
                    {
                        "tw:overflow-y-hidden": subCategoriesActive,
                        "tw:overflow-y-auto": !subCategoriesActive
                    }
                )}
            >
                <SidebarContextProvider
                    rootCategory={rootCategory}
                    accountLinks={parsedAccountLinks}
                    profileLink={profilelink}
                    innerHtml={innerHTML}
                    activeCategoryIndexes={activeCategoryIndexes}
                    assortimentOpen={assortimentOpen}
                    setAssortimentOpen={setAssortimentOpen}
                    setActiveCatetoryIndexes={setActiveCategoryIndexes}
                >
                    {children}
                </SidebarContextProvider>
            </Flyout.Content>
            <Flyout.Controls>
                <Flyout.Controls.CloseButton />
            </Flyout.Controls>
        </Flyout>
    );
};
